import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Settings, Save, Users, Key, Edit2, CheckCircle2, XCircle, Loader2, ExternalLink, ChevronDown, ChevronRight, Trash2, AlertTriangle, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ResendWizard from "@/components/ResendWizard";
import StripeWizard from "@/components/StripeWizard";

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    org_name: "Blue vs Blue",
    tagline: "Prostate Cancer Awareness Organization",
    mission: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    social_facebook: "",
    social_twitter: "",
    social_instagram: "",
    social_linkedin: "",
    social_youtube: "",
    stripe_api_key: "",
    sendgrid_api_key: "",
    resend_api_key: "",
    sender_email: "",
    sender_name: "Blue vs Blue",
    apple_developer_team_id: "",
    apple_pass_type_id: "",
    google_wallet_issuer_id: "",
    google_wallet_service_account: "",
    footer_text: "Blue vs Blue is a 501(c)(3) nonprofit organization. All donations are tax-deductible."
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "", password: "" });

  // Email setup checker state
  const [emailStatus, setEmailStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  const runEmailCheck = async () => {
    setChecking(true);
    try {
      const res = await axios.get(`${API}/settings/email-status`, { withCredentials: true });
      setEmailStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Health check failed");
    } finally { setChecking(false); }
  };

  const sendTestEmail = async () => {
    try {
      const res = await axios.post(`${API}/settings/test-email`, {}, { withCredentials: true });
      toast.success(`Test email sent to ${res.data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    }
  };

  // Cleanup state
  const [cleanupTargets, setCleanupTargets] = useState({ podcasts: false, streams: false, meetings: false });
  const [cleanupScope, setCleanupScope] = useState("ended_only");
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const [cleanupTyped, setCleanupTyped] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [stripeWizardOpen, setStripeWizardOpen] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  // Reconcile stuck payments state
  const [stuckPayments, setStuckPayments] = useState(null);
  const [loadingStuck, setLoadingStuck] = useState(false);
  const [reconcilingSession, setReconcilingSession] = useState("");
  const [manualSessionId, setManualSessionId] = useState("");

  const loadStuckPayments = async () => {
    setLoadingStuck(true);
    try {
      const res = await axios.get(`${API}/admin/payments/stuck`, { withCredentials: true });
      setStuckPayments(res.data);
      if (res.data.count === 0) toast.success("No stuck payments — all good!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load stuck payments");
    } finally { setLoadingStuck(false); }
  };

  const reconcileOne = async (session_id) => {
    setReconcilingSession(session_id);
    try {
      const res = await axios.post(`${API}/admin/payments/reconcile`, { session_id }, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.already_reconciled
          ? "Already reconciled — refreshing list"
          : `Reconciled! ${res.data.type === "ticket" ? `Created order ${res.data.order_id} with ${res.data.tickets_created} ticket(s)` : `Created donation ${res.data.donation_id}`}`);
        await loadStuckPayments();
      } else {
        toast.error(res.data.error || "Reconcile failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconcile failed");
    } finally { setReconcilingSession(""); }
  };

  const reconcileManual = async () => {
    const sid = manualSessionId.trim();
    if (!sid) { toast.error("Paste a session_id or payment_intent_id first"); return; }
    setReconcilingSession(sid);
    try {
      // Auto-detect: pi_ = payment_intent_id, cs_ = session_id
      const payload = sid.startsWith("pi_")
        ? { payment_intent_id: sid }
        : { session_id: sid };
      const res = await axios.post(`${API}/admin/payments/reconcile`, payload, { withCredentials: true });
      if (res.data.success) {
        const recovered = res.data.recovered_from_stripe ? " (recovered from Stripe — no DB record existed)" : "";
        toast.success(res.data.already_reconciled
          ? "Already reconciled"
          : `Reconciled${recovered} — ${res.data.type === "ticket" ? `order ${res.data.order_id}, ${res.data.tickets_created} ticket(s)` : `donation ${res.data.donation_id}`}`);
        setManualSessionId("");
        await loadStuckPayments();
      } else {
        toast.error(res.data.error || "Reconcile failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconcile failed");
    } finally { setReconcilingSession(""); }
  };

  const fixEventImages = async () => {
    if (!window.confirm("Scan all events and clear any broken image_url values (e.g., local file paths)?\n\nEvents will fall back to their uploaded media. Safe to run anytime.")) return;
    try {
      const res = await axios.post(`${API}/admin/events/fix-image-urls`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success(`Cleaned ${res.data.fixed_count} event(s) with bad image_url values`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fix event images");
    }
  };

  const reconcileAll = async () => {
    if (!window.confirm("Reconcile ALL stuck payments? Safe — only processes Stripe-confirmed paid sessions.")) return;
    setLoadingStuck(true);
    try {
      const res = await axios.post(`${API}/admin/payments/reconcile-all`, {}, { withCredentials: true });
      toast.success(`Checked ${res.data.checked} — reconciled ${res.data.reconciled.length}, skipped ${res.data.skipped.length}, failed ${res.data.failed.length}`);
      await loadStuckPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconcile-all failed");
    } finally { setLoadingStuck(false); }
  };

  const deleteStuck = async (session_id) => {
    if (!window.confirm(`Delete this orphaned payment record?\n\nUse this ONLY for test-mode leftovers or sessions Stripe doesn't know about. Won't delete if a real order/donation already exists.`)) return;
    setReconcilingSession(session_id);
    try {
      const res = await axios.delete(`${API}/admin/payments/stuck/${encodeURIComponent(session_id)}`, { withCredentials: true });
      if (res.data.success) {
        toast.success("Deleted orphaned payment record");
        await loadStuckPayments();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setReconcilingSession(""); }
  };

  const syncFromStripe = async () => {
    const days = window.prompt("How many days back to sync from Stripe? (default 7, max 30)", "7");
    if (!days) return;
    const d = Math.min(parseInt(days, 10) || 7, 30);
    setLoadingStuck(true);
    try {
      const res = await axios.post(`${API}/admin/payments/sync-from-stripe`, { days: d }, { withCredentials: true });
      toast.success(`Synced ${d} days — checked ${res.data.checked} Stripe sessions, reconciled ${res.data.reconciled_count} new order(s)/donation(s)`);
      await loadStuckPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally { setLoadingStuck(false); }
  };

  const runBackfill = async () => {
    if (!window.confirm("Re-attach orphaned free tickets to their users and create missing orders? This is safe to re-run.")) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/backfill/free-tickets`, {}, { withCredentials: true });
      const d = res.data;
      toast.success(`Backfill complete — repaired ${d.repaired_tickets} tickets, linked ${d.linked_to_users_by_email} to users, added ${d.wallet_entries_added} wallet entries, created ${d.orders_created} orders.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally { setBackfilling(false); }
  };

  const runCleanup = async () => {
    const targets = Object.entries(cleanupTargets).filter(([, v]) => v).map(([k]) => k);
    if (!targets.length) { toast.error("Select at least one item type"); return; }
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup`, { targets, scope: cleanupScope }, { withCredentials: true });
      const summary = Object.entries(res.data.result).map(([k, v]) => `${k}: ${v.deleted}`).join(", ");
      toast.success(`Cleaned up — ${summary}`);
      setCleanupConfirmOpen(false);
      setCleanupTargets({ podcasts: false, streams: false, meetings: false });
      setCleanupTyped("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
    } finally { setCleaning(false); }
  };

  useEffect(() => {
    fetchSettings();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/list`, { withCredentials: true });
      setUsers(res.data || []);
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/admin`, { withCredentials: true });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const saveUser = async () => {
    if (!editUser) return;
    try {
      await axios.put(`${API}/admin/users/${editUser.user_id}`, editForm, { withCredentials: true });
      toast.success("User updated");
      setEditUser(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="Settings">
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 h-32"></div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="Settings">
      <div className="p-8" data-testid="admin-settings">
        <div className="mb-8">
          <h1 className="text-heading-2 text-deep-blue">Settings</h1>
          <p className="text-slate-600">Manage your organization settings</p>
        </div>

        {/* User Management */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-deep-blue mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> User Management</h2>
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`user-row-${u.user_id}`}>
                <div>
                  <span className="font-semibold text-deep-blue">{u.name}</span>
                  <span className="text-sm text-slate-500 ml-2">{u.email}</span>
                  <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${u.role === 'super_admin' ? 'bg-deep-blue text-white' : 'bg-slate-200 text-slate-600'}`}>{u.role}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => { setEditUser(u); setEditForm({ name: u.name, email: u.email, role: u.role, password: "" }); }}><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
              </div>
            ))}
          </div>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm(p => ({...p, name: e.target.value}))} /></div>
              <div><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm(p => ({...p, email: e.target.value}))} /></div>
              <div><Label>Role</Label>
                <select value={editForm.role} onChange={(e) => setEditForm(p => ({...p, role: e.target.value}))} className="w-full border rounded-md px-3 py-2 text-sm">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="developer">Developer</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="master_admin">Master Admin</option>
                </select>
              </div>
              <div><Label>New Password (leave blank to keep current)</Label><Input type="password" value={editForm.password} onChange={(e) => setEditForm(p => ({...p, password: e.target.value}))} placeholder="••••••••" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                <Button onClick={saveUser} className="bg-deep-blue text-white">Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Organization Info */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-deep-blue mb-4">Organization Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="org_name">Organization Name</Label>
                <Input 
                  id="org_name"
                  value={settings.org_name}
                  onChange={(e) => setSettings({...settings, org_name: e.target.value})}
                  data-testid="org-name"
                />
              </div>
              <div>
                <Label htmlFor="tagline">Tagline</Label>
                <Input 
                  id="tagline"
                  value={settings.tagline}
                  onChange={(e) => setSettings({...settings, tagline: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="mission">Mission Statement</Label>
                <Textarea 
                  id="mission"
                  value={settings.mission}
                  onChange={(e) => setSettings({...settings, mission: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-deep-blue mb-4">Contact Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input 
                  id="contact_email"
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                  data-testid="contact-email"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input 
                  id="contact_phone"
                  value={settings.contact_phone}
                  onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input 
                  id="address"
                  value={settings.address}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-deep-blue mb-4">Social Media</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="social_facebook">Facebook URL</Label>
                <Input 
                  id="social_facebook"
                  value={settings.social_facebook}
                  onChange={(e) => setSettings({...settings, social_facebook: e.target.value})}
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <Label htmlFor="social_twitter">Twitter URL</Label>
                <Input 
                  id="social_twitter"
                  value={settings.social_twitter}
                  onChange={(e) => setSettings({...settings, social_twitter: e.target.value})}
                  placeholder="https://twitter.com/..."
                />
              </div>
              <div>
                <Label htmlFor="social_instagram">Instagram URL</Label>
                <Input 
                  id="social_instagram"
                  value={settings.social_instagram}
                  onChange={(e) => setSettings({...settings, social_instagram: e.target.value})}
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div>
                <Label htmlFor="social_linkedin">LinkedIn URL</Label>
                <Input 
                  id="social_linkedin"
                  value={settings.social_linkedin}
                  onChange={(e) => setSettings({...settings, social_linkedin: e.target.value})}
                  placeholder="https://linkedin.com/..."
                />
              </div>
              <div>
                <Label htmlFor="social_youtube">YouTube URL</Label>
                <Input 
                  id="social_youtube"
                  value={settings.social_youtube}
                  onChange={(e) => setSettings({...settings, social_youtube: e.target.value})}
                  placeholder="https://youtube.com/..."
                />
              </div>
            </div>
          </div>

          {/* Integration Keys */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-deep-blue mb-4">Integration Settings</h2>
            <p className="text-sm text-slate-500 mb-4">
              These keys are used for payment processing and email notifications.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="stripe_api_key">Stripe API Key</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setStripeWizardOpen(true)}
                    className="text-xs h-7"
                    data-testid="open-stripe-wizard-btn"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Setup Wizard
                  </Button>
                </div>
                <Input 
                  id="stripe_api_key"
                  type="password"
                  value={settings.stripe_api_key}
                  onChange={(e) => setSettings({...settings, stripe_api_key: e.target.value})}
                  placeholder="sk_live_..."
                  data-testid="stripe-key"
                />
                <p className="text-xs text-slate-500 mt-1">Use the wizard to validate the key, check account status, create the webhook, and run a $0.50 test charge — all in one place.</p>
              </div>
              <div>
                <Label htmlFor="resend_api_key">Resend API Key</Label>
                <Input 
                  id="resend_api_key"
                  type="password"
                  value={settings.resend_api_key || ""}
                  onChange={(e) => setSettings({...settings, resend_api_key: e.target.value})}
                  placeholder="re_..."
                  data-testid="resend-key"
                />
              </div>
              <div>
                <Label htmlFor="sender_email">Sender Email</Label>
                <Input 
                  id="sender_email"
                  type="email"
                  value={settings.sender_email}
                  onChange={(e) => setSettings({...settings, sender_email: e.target.value})}
                  placeholder="info@bluevsblue.org"
                />
                <p className="text-xs text-slate-500 mt-1">Must use your verified domain in Resend (e.g. <code>info@bluevsblue.org</code>).</p>
              </div>
              <div>
                <Label htmlFor="sender_name">Sender Name</Label>
                <Input 
                  id="sender_name"
                  value={settings.sender_name || ""}
                  onChange={(e) => setSettings({...settings, sender_name: e.target.value})}
                  placeholder="Blue vs Blue"
                />
                <p className="text-xs text-slate-500 mt-1">Display name shown to recipients.</p>
              </div>

              {/* === Resend Setup Guide & Health Checker === */}
              <div className="md:col-span-2 mt-2 border-t pt-4 space-y-4">
                {/* Setup guide (collapsible) */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowSetupGuide(s => !s)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-100 transition-colors"
                    data-testid="setup-guide-toggle"
                  >
                    <span className="font-semibold text-deep-blue flex items-center gap-2">
                      {showSetupGuide ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      First-time Resend setup — step by step (about 10 minutes incl. DNS)
                    </span>
                  </button>
                  {showSetupGuide && (
                    <div className="px-5 pb-4 pt-1 space-y-3 text-sm text-slate-700">
                      <ol className="list-decimal pl-5 space-y-2">
                        <li>
                          <strong>Create a Resend account</strong> (free tier — 3,000 emails/month, 100/day) at{" "}
                          <a href="https://resend.com/signup" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1">resend.com/signup <ExternalLink className="w-3 h-3" /></a>
                        </li>
                        <li>
                          <strong>Get an API key:</strong> Resend dashboard →{" "}
                          <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1">API Keys <ExternalLink className="w-3 h-3" /></a>
                          {" "}→ <em>Create API Key</em> → choose <em>Full Access</em>. Key starts with <code className="bg-white px-1 rounded">re_</code> — you'll only see it once, copy it.
                        </li>
                        <li>
                          <strong>Add &amp; verify your domain:</strong> Resend dashboard →{" "}
                          <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1">Domains → Add Domain <ExternalLink className="w-3 h-3" /></a>
                          {" "}→ enter <code className="bg-white px-1 rounded">bluevsblue.org</code>. Resend will give you 3 DNS records (1 SPF + 2 DKIM). Add them at your domain registrar (GoDaddy / Namecheap / Cloudflare) — usually under DNS Management.
                        </li>
                        <li>
                          <strong>Wait for DNS propagation</strong> (5 min – 1 hour) then click <em>Verify DNS Records</em> in Resend. All three should turn green ✓.
                        </li>
                        <li>
                          <strong>Paste the key into the fields above:</strong> API key into <em>Resend API Key</em>, your sending address into <em>Sender Email</em> (e.g. <code>info@bluevsblue.org</code>), then click <em>Save Settings</em> below.
                        </li>
                        <li>
                          <strong>Run the health check</strong> (button below) → it'll confirm key + domain are good. Then <strong>send yourself a test email</strong>.
                        </li>
                      </ol>
                      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                        <strong>Quick test (no DNS needed):</strong> While DNS propagates, you can use <code>onboarding@resend.dev</code> as your sender email — but it'll only deliver to the email you signed up with at Resend. Once your domain is verified, switch to your real address.
                      </div>
                    </div>
                  )}
                </div>

                {/* Health checker */}
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div>
                      <h3 className="font-semibold text-deep-blue">Email System Health Check</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Verifies your Resend API key works and your sending domain is verified — without actually sending an email.</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button type="button" onClick={() => setWizardOpen(true)} className="bg-gradient-to-r from-blue-600 to-deep-blue text-white" data-testid="open-resend-wizard-btn">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Setup Wizard
                      </Button>
                      <Button type="button" variant="outline" onClick={runEmailCheck} disabled={checking} data-testid="email-health-check-btn">
                        {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                        Run Health Check
                      </Button>
                      <Button type="button" onClick={sendTestEmail} className="bg-deep-blue text-white" data-testid="send-test-email-btn">
                        Send Test Email to Me
                      </Button>
                    </div>
                  </div>

                  {emailStatus && (
                    <div className="mt-3 space-y-2" data-testid="email-status-results">
                      <div className={`text-sm font-semibold ${emailStatus.all_ok ? 'text-green-700' : 'text-amber-700'}`}>
                        {emailStatus.all_ok ? '✓ All checks passed — emails will deliver.' : 'Some checks failed — see details below.'}
                      </div>
                      <ul className="space-y-2">
                        {emailStatus.checks.map(c => (
                          <li key={c.id} className="flex gap-2 items-start text-sm" data-testid={`check-${c.id}`}>
                            {c.ok
                              ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                              : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                            <div>
                              <p className={c.ok ? 'text-slate-800' : 'text-slate-900 font-medium'}>{c.label}</p>
                              <p className="text-xs text-slate-500">{c.detail}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!emailStatus && !checking && (
                    <p className="text-xs text-slate-500">Click <em>Run Health Check</em> to verify your configuration.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-deep-blue mb-4">Footer Content</h2>
            <div>
              <Label htmlFor="footer_text">Footer Disclaimer</Label>
              <Textarea 
                id="footer_text"
                value={settings.footer_text}
                onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          {/* Wallet Integration */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-deep-blue mb-4">Digital Wallet (Apple & Google)</h2>
            <p className="text-sm text-slate-500 mb-4">Configure these to enable "Add to Apple/Google Wallet" for event tickets.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-deep-blue text-sm">Apple Wallet</h3>
                <p className="text-xs text-slate-400">Requires Apple Developer Program ($99/yr)</p>
                <div>
                  <Label htmlFor="apple_developer_team_id">Team ID</Label>
                  <Input id="apple_developer_team_id" value={settings.apple_developer_team_id} onChange={(e) => setSettings({...settings, apple_developer_team_id: e.target.value})} placeholder="XXXXXXXXXX" />
                </div>
                <div>
                  <Label htmlFor="apple_pass_type_id">Pass Type ID</Label>
                  <Input id="apple_pass_type_id" value={settings.apple_pass_type_id} onChange={(e) => setSettings({...settings, apple_pass_type_id: e.target.value})} placeholder="pass.com.bluevsblue.ticket" />
                </div>
              </div>
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-deep-blue text-sm">Google Wallet</h3>
                <p className="text-xs text-slate-400">Requires Google Cloud service account</p>
                <div>
                  <Label htmlFor="google_wallet_issuer_id">Issuer ID</Label>
                  <Input id="google_wallet_issuer_id" value={settings.google_wallet_issuer_id} onChange={(e) => setSettings({...settings, google_wallet_issuer_id: e.target.value})} placeholder="1234567890" />
                </div>
                <div>
                  <Label htmlFor="google_wallet_service_account">Service Account Email</Label>
                  <Input id="google_wallet_service_account" value={settings.google_wallet_service_account} onChange={(e) => setSettings({...settings, google_wallet_service_account: e.target.value})} placeholder="wallet@project.iam.gserviceaccount.com" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-settings-btn">
              {saving ? "Saving..." : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </form>

        {/* === Danger Zone — Cleanup === */}
        <div className="mt-8 bg-white rounded-lg p-6 shadow-sm border-2 border-red-200">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-red-700">Danger Zone — Bulk Cleanup</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Delete podcasts, live streams, and conference meetings in bulk. This is useful at the start of a new season or when clearing test data.
            <span className="text-red-600 font-medium"> This cannot be undone.</span>
          </p>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">What to delete:</p>
              <div className="grid sm:grid-cols-3 gap-2">
                {[
                  { key: "podcasts", label: "Podcasts" },
                  { key: "streams", label: "Live Streams" },
                  { key: "meetings", label: "Conference Meetings" },
                ].map(({ key, label }) => (
                  <label key={key} className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer ${cleanupTargets[key] ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-testid={`cleanup-target-${key}`}>
                    <input
                      type="checkbox"
                      checked={cleanupTargets[key]}
                      onChange={(e) => setCleanupTargets(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Scope:</p>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="cleanup-scope" value="ended_only" checked={cleanupScope === "ended_only"} onChange={(e) => setCleanupScope(e.target.value)} data-testid="cleanup-scope-ended" />
                  <span className="text-sm">Only ended / completed (safer)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="cleanup-scope" value="all" checked={cleanupScope === "all"} onChange={(e) => setCleanupScope(e.target.value)} data-testid="cleanup-scope-all" />
                  <span className="text-sm text-red-600">Everything (including currently live)</span>
                </label>
              </div>
            </div>

            <Button
              type="button"
              variant="destructive"
              onClick={() => setCleanupConfirmOpen(true)}
              disabled={!Object.values(cleanupTargets).some(Boolean)}
              data-testid="open-cleanup-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-deep-blue mb-1">Reconcile Stuck Stripe Payments</h3>
              <p className="text-xs text-slate-500 mb-3">
                When a customer pays via Stripe but the success page polling failed, the payment sits in <code>payment_transactions</code> without creating an order/ticket/donation.
                Use this to verify each session directly with Stripe and create the matching records (and send the receipt email).
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                <Button type="button" variant="outline" onClick={loadStuckPayments} disabled={loadingStuck} data-testid="load-stuck-payments-btn">
                  {loadingStuck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Find Stuck Payments
                </Button>
                <Button type="button" variant="outline" onClick={reconcileAll} disabled={loadingStuck} data-testid="reconcile-all-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Reconcile All
                </Button>
                <Button type="button" variant="default" className="bg-deep-blue text-white" onClick={syncFromStripe} disabled={loadingStuck} data-testid="sync-from-stripe-btn">
                  {loadingStuck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Sync from Stripe
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 -mt-2 mb-3">
                <strong>Sync from Stripe</strong> = pulls ALL paid Checkout Sessions directly from your Stripe account
                and creates any missing orders/tickets/donations. Use this if a payment was taken but didn&apos;t show up in your
                dashboard or admin tickets — covers cases where the success page never loaded AND the webhook never fired.
              </p>

              {stuckPayments && (
                <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-3" data-testid="stuck-payments-result">
                  {stuckPayments.count === 0 ? (
                    <p className="text-sm text-green-700">✓ No stuck payments. Everything is reconciled.</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-slate-700 mb-2">{stuckPayments.count} stuck payment{stuckPayments.count !== 1 ? "s" : ""}:</p>
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {stuckPayments.stuck.map((p) => (
                          <div key={p.session_id} className="bg-white border border-slate-200 rounded p-3 text-xs">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-bold">${(p.amount || 0).toFixed(2)}</span>
                              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] uppercase font-bold">{p.type || "unknown"}</span>
                              {p.status && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">{p.status}</span>}
                              {p.session_id.startsWith("cs_test_") && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase font-bold" title="Test-mode session — Stripe live mode cannot reconcile this; safe to delete">TEST MODE</span>
                              )}
                            </div>
                            <div className="text-slate-600 mb-1">
                              {p.donor_name && <span className="mr-2">{p.donor_name}</span>}
                              {p.donor_email && <span className="text-slate-400 mr-2">{p.donor_email}</span>}
                              {p.quantity && <span className="mr-2">qty: {p.quantity}</span>}
                            </div>
                            <div className="font-mono text-[10px] text-slate-400 break-all mb-2">{p.session_id}</div>
                            <div className="text-[10px] text-slate-400 mb-2">{p.created_at}</div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => reconcileOne(p.session_id)}
                                disabled={reconcilingSession === p.session_id}
                                data-testid={`reconcile-${p.session_id.slice(0, 16)}`}
                              >
                                {reconcilingSession === p.session_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                                Reconcile
                              </Button>
                              {p.session_id.startsWith("cs_test_") && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => deleteStuck(p.session_id)}
                                  disabled={reconcilingSession === p.session_id}
                                  data-testid={`delete-stuck-${p.session_id.slice(0, 16)}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" /> Delete (test session)
                                </Button>
                              )}
                              {!p.session_id.startsWith("cs_test_") && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => deleteStuck(p.session_id)}
                                  disabled={reconcilingSession === p.session_id}
                                  data-testid={`delete-stuck-${p.session_id.slice(0, 16)}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" /> Delete orphan
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="border-t pt-3 mt-3">
                <p className="text-xs font-medium text-slate-700 mb-1">Or paste a Stripe session_id (<code className="text-[10px]">cs_…</code>) or payment_intent_id (<code className="text-[10px]">pi_…</code>):</p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="cs_live_…  or  pi_…"
                    value={manualSessionId}
                    onChange={(e) => setManualSessionId(e.target.value)}
                    className="font-mono text-xs"
                    data-testid="reconcile-manual-session-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={reconcileManual}
                    disabled={!manualSessionId.trim() || reconcilingSession === manualSessionId.trim()}
                    data-testid="reconcile-manual-btn"
                  >
                    {reconcilingSession === manualSessionId.trim() ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Reconcile
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Find IDs in <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer" className="text-blue-600 underline">Stripe → Payments</a>. We auto-detect which type you pasted.</p>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-deep-blue mb-1">Fix Broken Event Thumbnails</h3>
              <p className="text-xs text-slate-500 mb-3">
                If event thumbnails appear as broken-image icons (?), it&apos;s usually because an old image_url field has a local file path
                like <code className="text-[10px]">C:\Users\…</code>. Click below to clear all such bad values — events will use their uploaded media instead.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={fixEventImages}
                data-testid="fix-event-images-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> Fix Broken Event Thumbnails
              </Button>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-deep-blue mb-1">Fix Orphaned Free Tickets</h3>
              <p className="text-xs text-slate-500 mb-3">
                Some users got free tickets while not logged in or before the user-link was tracked.
                Click this to attach those tickets to user accounts (by matching email), populate user wallets, and create missing orders. Safe to re-run anytime.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={runBackfill}
                disabled={backfilling}
                data-testid="backfill-tickets-btn"
              >
                {backfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Run Free Ticket Backfill
              </Button>
            </div>
          </div>
        </div>

        {/* Cleanup confirm dialog */}
        <Dialog open={cleanupConfirmOpen} onOpenChange={(o) => { if (!o) { setCleanupConfirmOpen(false); setCleanupTyped(""); } }}>
          <DialogContent className="sm:max-w-md" data-testid="cleanup-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" /> Confirm Deletion
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                You are about to permanently delete{" "}
                <strong>{Object.entries(cleanupTargets).filter(([, v]) => v).map(([k]) => k).join(", ")}</strong>{" "}
                ({cleanupScope === "ended_only" ? "ended only" : "all, including live"}).
              </p>
              <p className="text-sm text-slate-700">Type <code className="bg-slate-100 px-1 rounded font-mono">DELETE</code> to confirm:</p>
              <Input
                value={cleanupTyped}
                onChange={(e) => setCleanupTyped(e.target.value)}
                placeholder="DELETE"
                data-testid="cleanup-confirm-input"
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setCleanupConfirmOpen(false); setCleanupTyped(""); }}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={cleanupTyped !== "DELETE" || cleaning}
                  onClick={runCleanup}
                  data-testid="cleanup-confirm-btn"
                >
                  {cleaning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Permanently Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <ResendWizard open={wizardOpen} onOpenChange={setWizardOpen} onComplete={runEmailCheck} />
        <StripeWizard open={stripeWizardOpen} onOpenChange={setStripeWizardOpen} />
      </div>
    </AdminLayout>
  );
}
